import { useSelector } from '@xstate/react';
import {
  type ActorRefFrom,
  type AnyActorRef,
  type AnyEventObject,
  type AnyMachineSnapshot,
  type AnyStateMachine,
  type AnyStateNode,
  type InspectionEvent,
  type SnapshotFrom,
  createActor,
  createMachine,
  enqueueActions,
} from 'xstate';
import { sessionSettled } from './auth';
import { type Toast, type ToastInput, type UiEvent, UI_ACTOR_ID, uiMachine } from './ui';

export interface FeatureRegistration<
  TId extends string = string,
  TMachine extends AnyStateMachine = AnyStateMachine,
  TEmits extends string = string,
  THandles extends string = string,
  TAccepts extends string = string,
> {
  id: TId;
  machine: TMachine;
  emits: readonly TEmits[];
  handles: readonly THandles[];
  accepts: readonly TAccepts[];
}

export function defineFeature<
  const TId extends string,
  TMachine extends AnyStateMachine,
  const TEmits extends string,
  const THandles extends string,
  const TAccepts extends string,
>(
  registration: FeatureRegistration<TId, TMachine, TEmits, THandles, TAccepts>,
): FeatureRegistration<TId, TMachine, TEmits, THandles, TAccepts> {
  return registration;
}

export type AcceptedBy<F extends FeatureRegistration> = F['accepts'][number];

type Registry = readonly FeatureRegistration[];

type EmittedBy<T extends Registry> = T[number]['emits'][number];
type HandledBy<T extends Registry> = T[number]['handles'][number];
type UnknownHandles<T extends Registry> = Exclude<HandledBy<T>, EmittedBy<T>>;

type RegistryCheck<T extends Registry> = [UnknownHandles<T>] extends [never]
  ? unknown
  : { 'handles names an event no registered feature emits': UnknownHandles<T> };

type FeatureRefs<T extends Registry> = {
  [F in T[number] as F['id']]: ActorRefFrom<F['machine']>;
};

type FeatureAccepts<T extends Registry> = {
  [F in T[number] as F['id']]: AcceptedBy<F>;
};

export type AppEvent<T extends Registry> = { type: EmittedBy<T> } | UiEvent;

export interface AppSystem<T extends Registry> {
  appActor: ReturnType<typeof createActor<AnyStateMachine>>;
  send: (event: AppEvent<T>) => void;
  feature: <Id extends keyof FeatureRefs<T> & string>(id: Id) => FeatureRefs<T>[Id];
  sendTo: <Id extends keyof FeatureAccepts<T> & string>(
    id: Id,
    event: { type: FeatureAccepts<T>[Id] } & Record<string, unknown>,
  ) => void;
  useFeature: <Id extends keyof FeatureRefs<T> & string, R>(
    id: Id,
    selector: (snapshot: SnapshotFrom<FeatureRefs<T>[Id]>) => R,
  ) => R;
  useOverlayOpen: (name: string) => boolean;
  useToasts: () => readonly Toast[];
  ui: {
    openOverlay: (name: string) => void;
    closeOverlay: (name: string) => void;
    closeAllOverlays: () => void;
    pushToast: (toast: ToastInput) => void;
    dismissToast: (id: string) => void;
  };
}

type UiActorRef = ActorRefFrom<typeof uiMachine>;

function assertRegistry(features: Registry): void {
  const seen = new Set<string>();
  for (const feature of features) {
    if (feature.id === UI_ACTOR_ID)
      throw new Error(`"${UI_ACTOR_ID}" is the platform's actor id; pick another feature id`);
    if (seen.has(feature.id)) throw new Error(`feature "${feature.id}" is registered twice`);
    seen.add(feature.id);
  }
}

function buildRoutes(features: Registry): ReadonlyMap<string, readonly string[]> {
  const routes = new Map<string, string[]>();
  for (const feature of features) {
    for (const type of feature.handles) {
      const targets = routes.get(type) ?? [];
      targets.push(feature.id);
      routes.set(type, targets);
    }
  }
  return routes;
}

function buildRootMachine(features: Registry, ensure: (id: string) => AnyActorRef) {
  const routes = buildRoutes(features);
  return createMachine({
    id: 'app',
    types: { events: {} as AnyEventObject },
    // The `ui` actor is the one machine no screen ever selects — every page reaches it through
    // `openOverlay` / `pushToast` — so it is the one machine the root still boots itself.
    invoke: { id: UI_ACTOR_ID, src: uiMachine, systemId: UI_ACTOR_ID },
    on: {
      '*': {
        actions: enqueueActions(({ enqueue, event }) => {
          if (event.type.startsWith('xstate.error.actor.')) {
            console.error(`[app] actor failed: ${event.type}`, (event as { error?: unknown }).error);
            return;
          }
          if (event.type.startsWith('ui.')) {
            enqueue.sendTo(UI_ACTOR_ID, event);
            return;
          }
          // An event addressed to a feature IS a use of it, so the target comes up BEFORE the
          // forward: a machine that has to react to a sibling is running at the moment it must.
          for (const target of routes.get(event.type) ?? []) enqueue.sendTo(ensure(target), event);
        }),
      },
    },
  });
}

type UiErrorReporter = (report: { source: string; name: string; message: string }) => void;

type InspectableSnapshot = {
  status?: string;
  value?: unknown;
  can?: (event: AnyEventObject) => boolean;
  _nodes?: AnyMachineSnapshot['_nodes'];
};

function isPlatformEvent(type: string): boolean {
  return type.startsWith('xstate.') || type.startsWith('ui.');
}

function stateValueLabel(stateValue: unknown): string {
  return typeof stateValue === 'string' ? stateValue : JSON.stringify(stateValue);
}

function reportUnhandledEvent(featureId: string, event: AnyEventObject, stateValue: unknown): void {
  const message =
    `feature "${featureId}" ignored event "${event.type}" in state "${stateValueLabel(stateValue)}": no ` +
    'transition handles it there, so the screen that sent it changed nothing. Add a transition for it in that ' +
    'state, or give the transition that led here a target whose state handles it.';
  console.error(`[app] ${message}`);
  const host = globalThis as { __onautoReportUiError?: UiErrorReporter };
  host.__onautoReportUiError?.({ source: 'unhandled-event', name: 'UnhandledMachineEvent', message });
}

function warnGuardRefusedEvent(featureId: string, event: AnyEventObject, stateValue: unknown): void {
  console.warn(
    `[app] feature "${featureId}" refused event "${event.type}" in state "${stateValueLabel(stateValue)}": its ` +
      'guard did not pass',
  );
}

function matchesEventDescriptor(eventType: string, descriptor: string): boolean {
  if (descriptor === eventType || descriptor === '*') return true;
  if (!descriptor.endsWith('.*')) return false;
  const descriptorTokens = descriptor.split('.');
  const eventTokens = eventType.split('.');
  for (let tokenIndex = 0; tokenIndex < descriptorTokens.length; tokenIndex += 1) {
    const descriptorToken = descriptorTokens[tokenIndex];
    if (descriptorToken === '*') return tokenIndex === descriptorTokens.length - 1;
    if (descriptorToken !== eventTokens[tokenIndex]) return false;
  }
  return false;
}

function hasTransitionCandidate(snapshot: Pick<AnyMachineSnapshot, '_nodes'>, eventType: string): boolean {
  return snapshot._nodes.some((node) =>
    [...node.transitions.keys()].some((descriptor) => matchesEventDescriptor(eventType, descriptor)),
  );
}

function initialDescendantsOf(node: AnyStateNode): AnyStateNode[] {
  if (node.type === 'parallel') {
    return [node, ...Object.values(node.states).flatMap((child) => initialDescendantsOf(child))];
  }
  const [initialChild] = node.initial.target;
  return initialChild ? [node, ...initialDescendantsOf(initialChild)] : [node];
}

function ancestorsOf(node: AnyStateNode): AnyStateNode[] {
  const ancestors: AnyStateNode[] = [];
  for (let current = node.parent; current; current = current.parent) ancestors.push(current);
  return ancestors;
}

function collectReachableStateNodes(machine: AnyStateMachine): Set<AnyStateNode> {
  const reachable = new Set<AnyStateNode>();
  const enter = (node: AnyStateNode): void => {
    for (const descendant of initialDescendantsOf(node)) {
      reachable.add(descendant);
      for (const ancestor of ancestorsOf(descendant)) reachable.add(ancestor);
    }
  };
  enter(machine.root);
  for (const node of reachable) {
    const candidates = [...[...node.transitions.values()].flat(), ...(node.always ?? [])];
    for (const target of candidates.flatMap((transition) => transition.target ?? [])) {
      if (!reachable.has(target)) enter(target);
    }
  }
  return reachable;
}

function allStateNodesOf(node: AnyStateNode): AnyStateNode[] {
  return [node, ...Object.values(node.states).flatMap((child) => allStateNodesOf(child))];
}

function nodeHandlesEvent(node: AnyStateNode, eventType: string): boolean {
  return [...node.transitions.keys()].some((descriptor) => matchesEventDescriptor(eventType, descriptor));
}

function hasReachableTransitionFor(reachable: ReadonlySet<AnyStateNode>, eventType: string): boolean {
  for (const node of reachable) {
    if (nodeHandlesEvent(node, eventType)) return true;
  }
  return false;
}

function initialLabel(root: AnyStateNode): string {
  const path: string[] = [];
  let node = root;
  let [child] = node.initial.target;
  while (child) {
    path.push(child.key);
    node = child;
    [child] = node.initial.target;
  }
  return path.length > 0 ? path.join('.') : root.key;
}

function unreachableHandlerMessage(
  featureId: string,
  type: string,
  unreachableHandlers: readonly AnyStateNode[],
  initial: string,
): string {
  const plural = unreachableHandlers.length > 1;
  const names = unreachableHandlers.map((node) => `"${node.key}"`).join(', ');
  return (
    `feature "${featureId}" accepts "${type}" but only state${plural ? 's' : ''} ${names} ` +
    `${plural ? 'handle' : 'handles'} it, and nothing reaches ${plural ? 'them' : 'that state'} from initial ` +
    `"${initial}"; give the transition that leads there a target (a root-level target is written ` +
    `".${unreachableHandlers[0].key}"), or handle the event in a reachable state`
  );
}

function validateFeatureContracts(features: Registry): void {
  const misses: string[] = [];
  for (const feature of features) {
    const seen = new Set<string>();
    const reachable = collectReachableStateNodes(feature.machine);
    for (const type of feature.accepts) {
      if (seen.has(type)) {
        misses.push(`feature "${feature.id}" declares "${type}" in accepts more than once`);
        continue;
      }
      seen.add(type);
      if (feature.handles.includes(type)) {
        misses.push(
          `feature "${feature.id}" accepts "${type}" but also handles it: a page event and a sibling's domain ` +
            'event cannot share a type',
        );
        continue;
      }
      if (hasReachableTransitionFor(reachable, type)) continue;
      const unreachableHandlers = allStateNodesOf(feature.machine.root).filter(
        (node) => !reachable.has(node) && nodeHandlesEvent(node, type),
      );
      misses.push(
        unreachableHandlers.length > 0
          ? unreachableHandlerMessage(feature.id, type, unreachableHandlers, initialLabel(feature.machine.root))
          : `feature "${feature.id}" accepts "${type}" but no state of its machine handles it`,
      );
    }
  }
  if (misses.length === 0) return;
  const message = misses.join('\n');
  console.error(`[app] ${message}`);
  const host = globalThis as { __onautoReportUiError?: UiErrorReporter };
  host.__onautoReportUiError?.({ source: 'feature-contract', name: 'FeatureContractViolation', message });
  throw new Error(message);
}

function watchForUnhandledEvents(
  appActor: AnyActorRef,
  live: ReadonlyMap<string, AnyActorRef>,
  started: ReadonlySet<string>,
): void {
  appActor.system.inspect((inspection: InspectionEvent) => {
    if (inspection.type !== '@xstate.event' || isPlatformEvent(inspection.event.type)) return;
    const featureId = (inspection.actorRef as AnyActorRef).id;
    if (live.get(featureId) !== inspection.actorRef) return;
    if (!started.has(featureId)) return;
    const snapshot = inspection.actorRef.getSnapshot() as InspectableSnapshot;
    const nodes = snapshot._nodes;
    if (snapshot.status !== 'active' || typeof snapshot.can !== 'function' || !nodes) return;
    if (!hasTransitionCandidate({ _nodes: nodes }, inspection.event.type)) {
      reportUnhandledEvent(featureId, inspection.event, snapshot.value);
      return;
    }
    if (!snapshot.can(inspection.event)) warnGuardRefusedEvent(featureId, inspection.event, snapshot.value);
  });
}

function startOnceSessionSettled(actor: { start: () => unknown }, onStarted?: () => void): void {
  if (typeof window === 'undefined') {
    actor.start();
    onStarted?.();
    return;
  }
  void sessionSettled().then(() => {
    actor.start();
    onStarted?.();
  });
}

export function createAppSystem<const T extends Registry>(features: T & RegistryCheck<T>): AppSystem<T> {
  assertRegistry(features);
  validateFeatureContracts(features);
  const machines = new Map<string, AnyStateMachine>(features.map((entry) => [entry.id, entry.machine]));
  const live = new Map<string, AnyActorRef>();
  const started = new Set<string>();

  /**
   * A FEATURE'S LIFETIME IS DERIVED FROM ITS USE. Every authored machine is `initial: 'loading'`
   * with its read invoked on entry, so a root that booted the whole registry fired every screen's
   * read on every page: a student's page ran the tutor-only reads, the API refused them, and
   * `enterAfterDeniedRead` bounced the student off a page they were entitled to (measured:
   * `/completed-sessions` → refused `browseSubjects` → home; thirteen calls for a page needing one).
   * A machine now comes up when a screen SELECTS it, when a sibling's event is routed to it, or
   * when `send` addresses it — and never otherwise.
   *
   * Starting an actor is an imperative side effect on the actor system, not React state, so this is
   * called straight from render (`useFeature` → `feature(id)`) and has to be idempotent: the ledger
   * is what makes StrictMode's double render — and a second page selecting the same feature — one
   * machine and one read. It keeps a machine that has stopped stopped, rather than re-running a
   * read that already failed on every re-render.
   *
   * Created rather than `spawnChild`-ed from a root action for one reason: the root does not start
   * until the session has settled, and a page renders and selects BEFORE that. An event sent to an
   * unstarted root only queues, so a spawn asked for from render would answer the page nothing at
   * all. Created here, the machine is a real ref from its first selection — it holds the events
   * sent to it and starts, with the root, once the session has landed. `parent` is what keeps
   * `sendParent` and `xstate.error.actor.*` pointed at the root, and `systemId` is what keeps
   * `id === systemId === the feature id`.
   */
  function ensure(id: string): AnyActorRef {
    const already = live.get(id);
    if (already) return already;
    const actor = createActor(machines.get(id) as AnyStateMachine, { id, systemId: id, parent: appActor });
    live.set(id, actor);
    startOnceSessionSettled(actor, () => started.add(id));
    return actor;
  }

  const appActor = createActor(buildRootMachine(features, ensure));
  watchForUnhandledEvents(appActor, live, started);
  startOnceSessionSettled(appActor);
  const uiRef = appActor.system.get(UI_ACTOR_ID) as UiActorRef;
  const send = (event: AppEvent<T>): void => appActor.send(event);
  const feature = <Id extends keyof FeatureRefs<T> & string>(id: Id): FeatureRefs<T>[Id] =>
    ensure(id) as FeatureRefs<T>[Id];
  const sendTo = <Id extends keyof FeatureAccepts<T> & string>(
    id: Id,
    event: { type: FeatureAccepts<T>[Id] } & Record<string, unknown>,
  ): void => ensure(id).send(event as AnyEventObject);
  return {
    appActor,
    send,
    feature,
    sendTo,
    useFeature: (id, selector) =>
      useSelector(feature(id) as AnyActorRef, selector as (snapshot: unknown) => ReturnType<typeof selector>),
    useOverlayOpen: (name) => useSelector(uiRef, (snapshot) => snapshot.context.overlays.includes(name)),
    useToasts: () => useSelector(uiRef, (snapshot) => snapshot.context.toasts),
    ui: {
      openOverlay: (name) => send({ type: 'ui.overlay.open', name }),
      closeOverlay: (name) => send({ type: 'ui.overlay.close', name }),
      closeAllOverlays: () => send({ type: 'ui.overlay.closeAll' }),
      pushToast: (toast) => send({ type: 'ui.toast.push', toast }),
      dismissToast: (id) => send({ type: 'ui.toast.dismiss', id }),
    },
  };
}
